import { FC, memo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useTaskDetailRelatedInfoListContext } from '../../taskDetailRelatedInfoList'
import { RelatedInfoTable } from './components/relatedInfoTable/relatedInfoTable'
import { SuggestionTable } from './components/suggestionTable/suggestionTable'
import { useTranslation } from '@crew/modules/i18n'
import { CrewAiAssistantAvatar } from 'components/elements/crewAiAssistantAvatar/crewAiAssistantAvatar'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { useSubscribeTaskRelatedInfo } from '@crew/providers/websocket'
import { RelatedSuggestion } from '@crew/models/domain'
import { EntityType } from '@crew/enums/domain'

export const TaskDetailRelatedInfoListPanel: FC = memo(() => {
  const { t } = useTranslation()
  const { taskId } = useParams()

  if (!taskId) {
    throw new Error('taskId is undefined')
  }

  const { displayState, setDisplayState, setRelatedSuggestions } =
    useTaskDetailRelatedInfoListContext()

  // 表示制御
  const showSearching = displayState === 'searching'
  const showSuggestionTable = displayState === 'suggestion'

  // タスク関連情報提案作成完了したときのコールバック関数
  const handleTaskRelatedSuggestionCreated = useCallback(
    (relatedSuggestion: RelatedSuggestion) => {
      // 受信したsuggestionが現在表示しているタスクのものか確認
      if (
        relatedSuggestion.sourceEntityType !== EntityType.Task ||
        relatedSuggestion.sourceEntityRecordId !== taskId
      ) {
        return
      }

      const suggestionItems = relatedSuggestion.relatedItems.map(
        ({ entityType, entityRecordId, title, updatedBy, updatedAt }) => ({
          id: `${entityType}|${entityRecordId}`,
          entityType,
          entityRecordId,
          title,
          updatedBy,
          updatedAt,
        })
      )
      setRelatedSuggestions(suggestionItems)
      // 表示を関連情報提案テーブルに切り替え
      setDisplayState('suggestion')
    },
    [setDisplayState, setRelatedSuggestions, taskId]
  )

  // タスク関連情報提案作成完了をsubscribeする
  useSubscribeTaskRelatedInfo(taskId, handleTaskRelatedSuggestionCreated)

  // 検索中の場合
  if (showSearching) {
    return (
      <div className="flex flex-row flex-1 items-center justify-center gap-3">
        <CrewAiAssistantAvatar
          size={CrewAvatarSize.base}
          displayName={''} // ホバーしても何も表示しないため空文字列を設定
          cacheValue={''} // キャッシュを無効化するため空文字列を設定
        />
        {t('label.aiIsSearchingRelatedInfo')}
      </div>
    )
  }

  return (
    <>
      {showSuggestionTable ? (
        <div className="p-2.5">
          {/* 関連情報提案テーブルの表示 */}
          <SuggestionTable />
        </div>
      ) : (
        <div className="p-2.5">
          {/* 関連情報テーブルの表示 */}
          <RelatedInfoTable />
        </div>
      )}
    </>
  )
})
